import {clx, LabelRegular, Table, useSortableTable, type TableColumn} from '@hm/fundamental'
import {useCallback, useMemo} from 'react'

import {useRiskSurveyTranslation} from '../../hooks'
import {formatDate, type SortDirection} from '../../helpers'
import type {RiaListItemDto} from '../../types'

import {RiaPriorityPill} from './RiaPriorityPill'
import {RiaStatusPill} from './RiaStatusPill'

type RiaTableProps = {
  rias: RiaListItemDto[]
  selectedRiaId?: string
  onRiaSelect: (ria: RiaListItemDto) => void
  onSortChange?: (sortBy: string, sortDir: SortDirection) => void
}

export const RiaTable = ({rias, selectedRiaId, onRiaSelect, onSortChange}: RiaTableProps) => {
  const {t, i18n} = useRiskSurveyTranslation()

  const columns: TableColumn<RiaListItemDto>[] = useMemo(
    () => [
      {
        id: 'riNumber',
        label: t('table.riNumber'),
        sortable: true,
        render: (row) => {
          const isSelected = selectedRiaId === row.id

          return (
            <div className="flex items-center gap-2 min-w-[110px]">
              {isSelected && <span className="w-2 h-2 rounded-full bg-primary" />}
              <LabelRegular className={clx(isSelected && 'font-semibold', 'whitespace-nowrap')}>
                {row.riNumber}
              </LabelRegular>
            </div>
          )
        }
      },
      {id: 'title', label: t('table.title'), sortable: true},
      {
        id: 'priority',
        label: t('table.priority'),
        sortable: true,
        render: (row) => <RiaPriorityPill priority={row.priority} />
      },
      {
        id: 'costClassification',
        label: t('table.costClassification'),
        sortable: true,
        render: (row) => t(`costClassification.${row.costClassification}`)
      },
      {
        id: 'status',
        label: t('table.status'),
        sortable: true,
        render: (row) => <RiaStatusPill status={row.status} />
      },
      {
        id: 'lePrior',
        label: t('table.lePrior'),
        sortable: true,
        render: (row) =>
          row.lePrior !== null && row.lePrior !== undefined
            ? row.lePrior.toLocaleString(i18n.language)
            : '—'
      },
      {
        id: 'leAfter',
        label: t('table.leAfter'),
        sortable: true,
        render: (row) =>
          row.leAfter !== null && row.leAfter !== undefined
            ? row.leAfter.toLocaleString(i18n.language)
            : '—'
      },
      {
        id: 'leDelta',
        label: t('table.leDelta'),
        sortable: true,
        render: (row) =>
          row.leDelta !== null && row.leDelta !== undefined
            ? row.leDelta.toLocaleString(i18n.language)
            : '—'
      },
      {
        id: 'indicativeTimeScale',
        label: t('table.indicativeTimeScale'),
        sortable: true,
        render: (row) =>
          row.indicativeTimeScale ? formatDate(row.indicativeTimeScale, i18n.language) : '—'
      },
      {
        id: 'commentCount',
        label: t('table.comments'),
        render: (row) => row.commentCount
      }
    ],
    [i18n.language, selectedRiaId, t]
  )

  const config = useSortableTable<RiaListItemDto>(
    columns,
    [],
    {prefix: 'ria-row', postfixFieldName: 'id'},
    undefined,
    onSortChange
      ? (sortKey, direction) => onSortChange(sortKey, direction as SortDirection)
      : undefined
  )

  const handleRowClick = useCallback(
    (row: RiaListItemDto) => {
      onRiaSelect(row)
    },
    [onRiaSelect]
  )

  return (
    <div className="[&_thead_th]:text-base [&_thead_th]:font-semibold [&_thead_th]:leading-normal [&_thead_th_*]:text-base [&_thead_th_*]:font-semibold [&_thead_th_*]:leading-normal [&_thead_th:first-child]:whitespace-nowrap [&_thead_th:first-child]:min-w-[110px] [&_tbody_td:first-child]:whitespace-nowrap [&_tbody_td:first-child]:min-w-[110px]">
      <Table
        config={config}
        data={rias}
        getRowId={(row) => row.id}
        breakpoint="sm"
        onRowClick={handleRowClick}
        data-cy="ria-table"
      />
    </div>
  )
}
